import React from 'react';
import VisaPageLayout, { FaqItem, ProcessStep, RequirementItem, VisaPlan } from './visa-page-layout';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';

const plans: VisaPlan[] = [
  { id: 'kw-90', name: 'Tourist eVisa', entryType: 'Single Entry', validity: 'Valid for 3 months from issue', stay: 'Up to 90 days in Kuwait', processing: '2–4 business days', price: '3', currency: 'KWD' },
  { id: 'kw-90m', name: 'Tourist eVisa', entryType: 'Multiple Entry', validity: 'Valid for 6 months from issue', stay: 'Up to 90 days per visit', processing: '3–5 business days', price: '5', currency: 'KWD', featured: true },
  { id: 'kw-express', name: 'Tourist eVisa — Express', entryType: 'Single Entry', validity: 'Valid for 3 months from issue', stay: 'Up to 90 days in Kuwait', processing: 'Within 24–48 hours', price: '8', currency: 'KWD' },
];

const requirements: RequirementItem[] = [
  { label: 'Passport copy', detail: 'Valid for at least 6 months from your arrival date.' },
  { label: 'Passport-size photo', detail: 'Recent, plain white background, matching standard visa specifications.' },
  { label: 'Confirmed return ticket', detail: 'Round-trip or onward flight booking showing your departure from Kuwait.' },
  { label: 'Hotel booking or sponsor details', detail: 'Confirmed accommodation, or your host\'s Civil ID if staying privately.' },
  { label: 'Employment or travel history (if requested)', detail: 'Occasionally requested for first-time applicants from certain nationalities.' },
];

const goodToKnow: string[] = [
  'Kuwait\'s tourist eVisa is available to a specific list of eligible nationalities — we\'ll confirm your eligibility before you pay anything.',
  'The multiple-entry option is popular with travelers doing business across the wider Gulf region.',
  'Kuwait observes strict customs rules on alcohol and certain medications — we include a short guide with every approved visa.',
  'Summer temperatures are extreme; we can help time your visa validity around cooler travel months if useful.',
];

const steps: ProcessStep[] = [
  { title: 'Upload your documents', text: 'Send your passport scan, photo and travel details through our secure form.' },
  { title: 'We review & submit', text: 'Our team checks your file against current Kuwait eVisa requirements before filing.' },
  { title: 'Track your status', text: 'We\'ll update you by WhatsApp or email while your application is processed.' },
  { title: 'Receive your eVisa', text: 'Download your approved eVisa and carry a printed copy with your passport.' },
];

const faqs: FaqItem[] = [
  { question: 'Is my nationality eligible for the Kuwait tourist eVisa?', answer: 'Eligibility is limited to a specific list of countries. Send us your passport nationality and we\'ll confirm before you start the application.' },
  { question: 'Can I extend my stay once I\'m in Kuwait?', answer: 'Extensions are handled directly through Kuwait\'s immigration authority and aren\'t guaranteed. We recommend applying for the visa length you actually need.' },
  { question: 'How fast is the express option really?', answer: 'Most express applications are approved within 24–48 hours, though this can vary slightly by nationality and time of year.' },
  { question: 'What should I know about local customs before I arrive?', answer: 'We send a short cultural and legal guide with every approved visa covering dress code, alcohol restrictions, and everyday etiquette.' },
];

export default function VisaKuwaitPage() {

  const { plans, loading, error } = useVisaPacksByCountry('KWT');
  
  return (
    <VisaPageLayout
      countryName="Kuwait"
      flagEmoji="🇰🇼"
      heroEyebrow="State of Kuwait"
      heroImage="https://images.unsplash.com/photo-1663900108404-a05e8bf82cda?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="Kuwait"
      heroTitleTail="visa, handled from start to finish"
      heroSubtitle="Whether it's business, family, or your first visit to the Gulf — we prepare your Kuwait tourist eVisa so the details are already sorted when you land."
      trustStats={[
        { value: '2–5', label: 'Business Days' },
        { value: '95%', label: 'Approval Rate' },
        { value: '1,400+', label: 'Kuwait Visas Issued' },
      ]}
      plans={plans}
      plansLoading={loading}
      plansError={error}
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      faqs={faqs}
      ctaSubtitle="Send your documents today and we'll confirm your eligibility and next steps within one business day."
    />
  );
}